import { CommonModule } from '@angular/common';
import { environment } from 'src/environments/environment';
import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  QueryList,
  ViewChildren,
  createNgModule,
  inject
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { InputComponent } from 'src/app/components/shared/input';
import { DropdownComponent } from 'src/app/components/shared/dropdown';
import { TrashComponent } from 'src/app/components/icons/trash';
import { PlusComponent } from 'src/app/components/icons/plus';
import { LocaleService } from 'src/app/services/locale';
import { CategoryService } from 'src/app/services/category';
import {
  ClimatiqService,
  DeepLTranslationResponse
} from 'src/app/services/climatiq';
import { forkJoin, from, Observable, of, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  finalize,
  map,
  catchError
} from 'rxjs/operators';
import { HttpClient, HttpClientModule, HttpParams } from '@angular/common/http';

@Component({
  selector: 'app-category6-question1',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputComponent,
    DropdownComponent,
    TrashComponent,
    PlusComponent,
    HttpClientModule
  ],
  template: `
    <!-- Applicability toggle -->
    <!-- <button (click)="italianToEnglish()">Test Italian → English</button> -->

    <div class="flex flex-wrap gap-4 sm:gap-6 mt-4">
      <div
        (click)="toggleApplicability(true)"
        class="cursor-pointer flex gap-2 items-center"
      >
        <div
          [class.bg-green-950]="applicable"
          class="w-4 h-4 border rounded-full flex-shrink-0"
        ></div>
        <span>{{ strings[language].choice_yes }}</span>
      </div>
      <div
        (click)="toggleApplicability(false)"
        class="cursor-pointer flex gap-2 items-center"
      >
        <div
          [class.bg-green-950]="!applicable"
          class="w-4 h-4 border rounded-full flex-shrink-0"
        ></div>
        <span>{{ strings[language].choice_no }}</span>
      </div>
    </div>

    <!-- Add Emission Source -->
    <div class="flex justify-end mb-2 items-center" *ngIf="applicable">
      <div
        (click)="addEmissionSource()"
        class="bg-[#F8F9FA] px-3 py-2 rounded text-white text-md cursor-pointer"
      >
        <app-plus></app-plus>
      </div>
    </div>

    <form *ngIf="applicable" [formGroup]="form" class="space-y-10 mt-4">
      <div formArrayName="emissionSources" class="space-y-6">
        <div
          *ngFor="let source of emissionSources.controls; let i = index"
          [formGroupName]="i"
          class="border p-4 rounded-lg space-y-4"
        >
          <div class="flex justify-between items-center">
            <h2 class="text-lg font-semibold">
              {{ strings[language].source_label }} {{ i + 1 }}
            </h2>
            <div
              class="cursor-pointer"
              *ngIf="emissionSources.length > 1"
              (click)="removeEmissionSource(i)"
            >
              <app-trash></app-trash>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- Standard Factor Value (auto-filled) -->
            <div>
              <label class="block text-sm font-medium mb-1">{{
                strings[language].label_description
              }}</label>
              <app-input
                [placeholder]="strings[language].placeholder_description"
                [control]="getFormControl(source, 'description')"
              ></app-input>
            </div>
            <!-- Activity Value -->
            <div>
              <label class="block text-sm font-medium mb-1">{{
                strings[language].label_activity_value
              }}</label>
              <app-input
                type="number"
                [placeholder]="strings[language].placeholder_activity_value"
                [control]="getFormControl(source, 'activity_value')"
              ></app-input>
            </div>

            <!-- Activity Unit -->
            <div>
              <label class="block text-sm font-medium mb-1">{{
                strings[language].label_activity_unit
              }}</label>
              <app-dropdown
                [options]="getUnitOptionsForSource(i)"
                [placeholder]="strings[language].placeholder_activity_unit"
                [control]="getFormControl(source, 'activity_unit')"
              ></app-dropdown>
            </div>
            <!-- Custom Factor Toggle -->
            <div class="col-span-2 flex items-center gap-2">
              <div
                class="flex items-center gap-2 cursor-pointer"
                (click)="toggleCheckbox(source, 'custom_factor')"
              >
                <div
                  class="w-5 h-5 border border-gray-500 rounded-sm flex items-center justify-center"
                  [ngClass]="{
                    'bg-[#183A25]': getFormControl(source, 'custom_factor')
                      .value
                  }"
                >
                  <svg
                    *ngIf="getFormControl(source, 'custom_factor')?.value"
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-3 h-3 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    stroke-width="3"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </div>

              <label for="customFactor{{ i }}">{{
                strings[language].label_custom_factor
              }}</label>
            </div>
            <div>
              <label class="block text-sm font-medium mb-1">{{
                strings[language].label_factor_value
              }}</label>
              <div
                [style.pointer-events]="
                  getFormControl(source, 'custom_factor').value
                    ? 'none'
                    : 'auto'
                "
                [style.opacity]="
                  getFormControl(source, 'custom_factor').value ? '0.5' : '1'
                "
                #closeDropdown
                class="col-span-2 relative"
              >
                <app-input
                  [placeholder]="strings[language].placeholder_factor_value"
                  [control]="getFormControl(source, 'factor_value')"
                  maxlength="120"
                  (input)="searchDescription($event, i)"
                ></app-input>

                <div
                  style="display: block;"
                  *ngIf="showSuggestions && selectedSourceIndex === i"
                  class="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
                >
                  <div class="py-1">
                    <div
                      *ngIf="isLoadingSuggestions"
                      class="px-4 py-2 text-center text-gray-500"
                    >
                      {{ strings[language].emissionFactorPlaceHolder }}
                    </div>
                    <div
                      *ngIf="!isLoadingSuggestions && suggestions.length === 0"
                      class="px-4 py-2 text-center text-gray-500"
                    >
                      {{ strings[language].noSuggestionsFound }}
                    </div>
                    <div
                      *ngFor="let suggestion of suggestions"
                      (click)="selectSuggestion(suggestion)"
                      class="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    >
                      <div class="font-medium">
                        {{
                          language === 'italian'
                            ? suggestion.name_it
                            : suggestion.name_en
                        }}
                      </div>
                      <div class="text-sm text-gray-500">
                        {{ suggestion.unit }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- Custom Factor Fields (shown when custom is true) -->
            <!-- *ngIf="getFormControl(source, 'custom_factor').value" -->
            <ng-container>
              <!-- Custom Factor Value -->
              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].label_custom_value
                }}</label>
                <div
                  [style.pointer-events]="
                    !getFormControl(source, 'custom_factor').value
                      ? 'none'
                      : 'auto'
                  "
                  [style.opacity]="
                    !getFormControl(source, 'custom_factor').value ? '0.5' : '1'
                  "
                >
                  <app-input
                    type="number"
                    [placeholder]="strings[language].placeholder_custom_value"
                    [control]="getFormControl(source, 'custom_value')"
                  ></app-input>
                </div>
              </div>

              <!-- Factor Unit -->

              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].label_factor_unit
                }}</label>
                <div
                  [style.pointer-events]="
                    !getFormControl(source, 'custom_factor').value
                      ? 'none'
                      : 'auto'
                  "
                  [style.opacity]="
                    !getFormControl(source, 'custom_factor').value ? '0.5' : '1'
                  "
                >
                  <app-dropdown
                    [options]="activityUnitOptions"
                    [placeholder]="strings[language].placeholder_factor_unit"
                    [control]="getFormControl(source, 'factor_unit')"
                  ></app-dropdown>
                </div>
              </div>

              <!-- Factor Source URL -->
              <div>
                <label class="block text-sm font-medium mb-1">{{
                  strings[language].label_factor_source
                }}</label>
                <div
                  class="col-span-2"
                  [style.pointer-events]="
                    !getFormControl(source, 'custom_factor').value
                      ? 'none'
                      : 'auto'
                  "
                  [style.opacity]="
                    !getFormControl(source, 'custom_factor').value ? '0.5' : '1'
                  "
                >
                  <app-input
                    type="url"
                    [placeholder]="strings[language].placeholder_factor_source"
                    [control]="getFormControl(source, 'factor_source')"
                  ></app-input>
                  <div
                    *ngIf="
                      getFormControl(source, 'factor_source').errors
                        ?.['required'] &&
                      getFormControl(source, 'factor_source').touched
                    "
                    class="text-red-500 text-xs mt-1"
                  >
                    {{ strings[language].error_required }}
                  </div>
                  <div
                    *ngIf="
                      getFormControl(source, 'factor_source').errors?.['invalidUrl']
                    "
                    class="text-red-500 text-xs mt-1"
                  >
                    {{ strings[language].error_invalid_url }}
                  </div>
                </div>
              </div>
            </ng-container>

            <!-- Justification -->
            <div class="col-span-2">
              <label class="block text-sm font-medium mb-1">{{
                strings[language].label_justification
              }}</label>
              <textarea
                [placeholder]="strings[language].placeholder_justification"
                class="w-full border border-[#CED4DA] rounded p-2"
                rows="3"
                [formControl]="getFormControl(source, 'justification')"
              ></textarea>
            </div>
          </div>
        </div>
      </div>
    </form>
  `
})
export class Category6Question1Component implements OnInit {
  @ViewChildren('closeDropdown') dropdownContainers!: QueryList<ElementRef>;
  language = inject(LocaleService).getLanguage();
  categoryService = inject(CategoryService);
  private climatiqService = inject(ClimatiqService);

  form!: FormGroup;
  applicable = false;
  suggestions: any[] = [];
  showSuggestions = false;
  selectedSourceIndex = -1;
  isLoadingSuggestions = false;
  private descriptionSearchTerms = new Subject<string>();

  strings: any = {
    english: {
      guidance_text:
        'What goes in Category 6? Use this section only for indirect GHG sources or sinks that do not logically belong in Categories 3 to 5.',
      choice_yes: 'Yes',
      choice_no: 'No',
      source_label: 'Emission Source',
      label_description: 'Source description',
      label_activity_value: 'Activity value',
      label_activity_unit: 'Unit',
      label_factor_value: 'Emission factor',
      label_custom_factor: 'Use custom emission factor',
      label_custom_value: 'Custom value',
      label_factor_unit: 'Factor unit',
      label_factor_source: 'Factor source / URL',
      label_justification: 'Justification',

      // Placeholders
      placeholder_description:
        'Plain-language description of the source (max 120 chars)',
      placeholder_activity_value: 'Enter activity value (e.g., kWh, t km, kg)',
      placeholder_activity_unit: 'Select unit',
      placeholder_factor_value: 'Auto-filled from factor library',
      placeholder_custom_value: 'Enter custom factor value (kg CO₂e / unit)',
      placeholder_factor_unit: 'Select factor unit',
      placeholder_factor_source: 'Enter source URL (https://...)',
      placeholder_justification: 'Enter justification here',
      error_required: 'Source URL is required when using custom factor',
      error_invalid_url:
        'Please enter a valid URL (must start with http:// or https://)',
      emissionFactorPlaceHolder: 'Loading suggestions...',
      noSuggestionsFound: 'No suggestions found'
    },
    italian: {
      guidance_text:
        'Cosa va nella Categoria 6? Usa questa sezione solo per fonti o pozzi di GHG indiretti che non appartengono logicamente alle Categorie da 3 a 5.',
      choice_yes: 'Sì',
      choice_no: 'No',
      source_label: 'Fonte di Emissione',
      label_description: 'Descrizione della fonte',
      label_activity_value: 'Valore attività',
      label_activity_unit: 'Unità',
      label_factor_value: 'Fattore di emissione',
      label_custom_factor: 'Usa fattore di emissione personalizzato',
      label_custom_value: 'Valore personalizzato',
      label_factor_unit: 'Unità del fattore',
      label_factor_source: 'Fonte / URL del fattore',
      label_justification: 'Giustificazione',

      // Placeholders
      placeholder_description:
        'Descrizione in linguaggio semplice della fonte (max 120 caratteri)',
      placeholder_activity_value:
        'Inserisci valore attività (es. kWh, t km, kg)',
      placeholder_activity_unit: 'Seleziona unità',
      placeholder_factor_value:
        'Compilato automaticamente dalla libreria dei fattori',
      placeholder_custom_value:
        'Inserisci valore fattore personalizzato (kg CO₂e / unità)',
      placeholder_factor_unit: 'Seleziona unità del fattore',
      placeholder_factor_source: 'Inserisci URL fonte (https://...)',
      placeholder_justification: 'Inserisci qui la giustificazione',
      error_required:
        "L'URL della fonte è obbligatorio quando si utilizza un fattore personalizzato",
      error_invalid_url:
        'Inserisci un URL valido (deve iniziare con http:// o https://)',
      emissionFactorPlaceHolder: 'Caricamento dei suggerimenti...',
      noSuggestionsFound: 'Nessun suggerimento trovato'
    }
  };

  activityUnitOptions = [
    { value: 'kWh', label: 'kWh' },
    { value: 'kg', label: 'kg' },
    { value: 'g', label: 'g' },
    { value: 'm3', label: 'm³' },
    { value: 'liter', label: 'Liter' }
  ];

  unitTypeOptions: { [key: string]: any[] } = {
    weight: [
      { value: 'g', label: 'g', labelIt: 'g' },
      { value: 'kg', label: 'kg', labelIt: 'kg' },
      { value: 't', label: 't (tonne)', labelIt: 't (tonnellata)' },
      { value: 'ton', label: 'ton (US)', labelIt: 'ton (USA)' },
      { value: 'lb', label: 'lb (pound)', labelIt: 'lb (libbra)' }
    ],

    volume: [
      { value: 'ml', label: 'ml', labelIt: 'ml' },
      { value: 'l', label: 'l', labelIt: 'l' },
      { value: 'm3', label: 'm³ (cubic metre)', labelIt: 'm³ (metro cubo)' },
      { value: 'cubic_foot', label: 'ft³ (cubic foot)', labelIt: 'ft³ (piede cubo)' },
      { value: 'gallon_us', label: 'gal (US gallon)', labelIt: 'gal (gallone USA)' },
      { value: 'bbl', label: 'bbl (barrel)', labelIt: 'bbl (barile)' }
    ],

    distance: [
      { value: 'm', label: 'm', labelIt: 'm' },
      { value: 'km', label: 'km', labelIt: 'km' },
      { value: 'mi', label: 'mi (mile)', labelIt: 'mi (miglio)' },
      { value: 'ft', label: 'ft (foot)', labelIt: 'ft (piede)' }
    ],

    money: [
      { value: 'usd', label: 'USD (US Dollar)', labelIt: 'USD (Dollaro statunitense)' },
      { value: 'eur', label: 'EUR (Euro)', labelIt: 'EUR (Euro)' },
      { value: 'gbp', label: 'GBP (Pound Sterling)', labelIt: 'GBP (Sterlina britannica)' },
      { value: 'cad', label: 'CAD (Canadian Dollar)', labelIt: 'CAD (Dollaro canadese)' },
      { value: 'aud', label: 'AUD (Australian Dollar)', labelIt: 'AUD (Dollaro australiano)' },
      { value: 'chf', label: 'CHF (Swiss Franc)', labelIt: 'CHF (Franco svizzero)' },
      { value: 'sek', label: 'SEK (Swedish Krona)', labelIt: 'SEK (Corona svedese)' },
      { value: 'nok', label: 'NOK (Norwegian Krone)', labelIt: 'NOK (Corona norvegese)' },
      { value: 'dkk', label: 'DKK (Danish Krone)', labelIt: 'DKK (Corona danese)' },
      { value: 'jpy', label: 'JPY (Japanese Yen)', labelIt: 'JPY (Yen giapponese)' },
      { value: 'cny', label: 'CNY (Chinese Yuan)', labelIt: 'CNY (Yuan cinese)' }
    ],

    number: [
      { value: 'item', label: 'Item', labelIt: 'Elemento' },
      { value: 'money', label: 'Money', labelIt: 'Soldi' },
      { value: 'number', label: 'Number', labelIt: 'Numero' }
    ]
  };
  constructor(private fb: FormBuilder, private http: HttpClient) { }

  ngOnInit(): void {
    this.form = this.fb.group({
      emissionSources: this.fb.array([])
    });
    this.form.valueChanges.subscribe(() => {
      this.saveToLocalStorage();
    });
    const categoriesRaw = localStorage.getItem('categories');
    let categories = [];

    if (categoriesRaw) {
      try {
        categories = JSON.parse(categoriesRaw);
        while (categories.length < 6)
          categories.push(this.createDefaultCategory());

        const answers = categories[5]?.questions?.[0]?.answers;
        if (answers && Array.isArray(answers)) {
          this.applicable = true;
          answers.forEach((ans: any) => {
            const source = this.fb.group({
              description: [
                ans.description || '',
                [
                  Validators.required,
                  Validators.maxLength(120),
                  this.noNumbersOnlyValidator
                ]
              ],
              activity_value: [
                ans.activity_value || '',
                [Validators.required, Validators.min(0)]
              ],
              activity_unit: [ans.activity_unit || '', Validators.required],
              factor_value: [ans.factor_value || ''],
              custom_factor: [ans.custom_factor || false],
              custom_value: [ans.custom_value || '', [Validators.min(0)]],
              factor_unit: [ans.factor_unit || ''],
              factor_source: [ans.factor_source || '', this.urlValidator],
              justification: [
                ans.justification || '',
                [Validators.required, Validators.minLength(30)]
              ],
              unit_type: [ans.unit_type || 'number']
            });
            if (ans.suggestion_id) {
              this.suggestion_id = ans.suggestion_id;
            }
            // Add this to properly initialize custom factor fields
            if (ans.custom_factor) {
              source
                .get('custom_value')
                ?.setValidators([Validators.required, Validators.min(0)]);
              source.get('factor_unit')?.setValidators([Validators.required]);
              source.get('factor_source')?.setValidators([Validators.required]);
            }

            this.emissionSources.push(source);
          });
        }
      } catch (e) {
        console.error('Failed to parse localStorage categories:', e);
      }
    }

    // Initialize suggestions subscription
    this.descriptionSearchTerms
      .pipe(
        debounceTime(600),
        distinctUntilChanged(),
        switchMap((term: string) => {
          this.isLoadingSuggestions = true;
          this.showSuggestions = true;
          return this.getTranslatedSuggestions(term);
        })
      )
      .subscribe({
        next: suggestions => {
          this.suggestions = suggestions;
          this.showSuggestions = this.suggestions.length > 0;
          this.isLoadingSuggestions = false;
        },
        error: err => {
          console.error('Error fetching suggestions:', err);
          this.suggestions = [];
          this.showSuggestions = false;
          this.isLoadingSuggestions = false;
        }
      });

    if (this.applicable && this.emissionSources.length === 0)
      this.addEmissionSource();
  }

  get emissionSources(): FormArray {
    return this.form.get('emissionSources') as FormArray;
  }
  private getTranslatedSuggestions(italianQuery: string): Observable<any[]> {
    return from(
      this.climatiqService.translateText(italianQuery, 'IT', 'EN')
    ).pipe(
      switchMap((translationResponse: DeepLTranslationResponse) => {
        if (!translationResponse?.translations?.[0]?.text) {
          return of([]);
        }

        const englishQuery = translationResponse.translations[0].text;
        return this.climatiqService.getSuggestions(englishQuery).pipe(
          switchMap((climatiqResponse: { results: any[] }) => {
            const suggestions = climatiqResponse?.results || [];

            if (suggestions.length === 0) {
              return of([]);
            }

            const translationObservables = suggestions.map(suggestion => {
              const baseSuggestion = {
                ...suggestion,
                name_en: suggestion?.emission_factor?.name || '',
                name_it: '',
                unit: suggestion?.emission_factor?.unit || '',
                unit_type: suggestion?.emission_factor?.unit_type || 'number',
                suggestion_id: suggestion?.suggestion_id || ''
              };

              if (!suggestion?.emission_factor?.name) {
                return of(baseSuggestion);
              }

              return from(
                this.climatiqService.translateText(
                  suggestion.emission_factor.name,
                  'EN',
                  'IT'
                )
              ).pipe(
                map((translation: DeepLTranslationResponse) => ({
                  ...baseSuggestion,
                  name_it:
                    translation?.translations?.[0]?.text ||
                    baseSuggestion.name_en
                })),
                catchError(() => of(baseSuggestion))
              );
            });

            return forkJoin(translationObservables);
          }),
          catchError(() => of([]))
        );
      }),
      catchError(() => of([]))
    );
  }

  getFormControl(group: AbstractControl, field: string): FormControl {
    return group.get(field) as FormControl;
  }

  toggleApplicability(value: boolean): void {
    this.applicable = value;
    if (value && this.emissionSources.length === 0) this.addEmissionSource();
    if (!value) {
      this.emissionSources.clear();
      this.saveToLocalStorage();
    }
  }

  addEmissionSource(): void {
    const source = this.fb.group({
      description: [
        '',
        [
          Validators.required,
          Validators.maxLength(120),
          this.noNumbersOnlyValidator
        ]
      ],
      activity_value: ['', [Validators.required, Validators.min(0)]],
      activity_unit: ['', Validators.required], // Default to kWh
      factor_value: [''],
      custom_factor: [false],
      custom_value: ['', [Validators.min(0)]],
      factor_unit: [''], // Default to kg CO2e
      factor_source: ['', this.urlValidator],
      justification: ['', [Validators.required, Validators.minLength(30)]],
      unitType: ['number'],
      unit_type: ['']
    });

    // Set up conditional validation for custom factor fields
    // In your addEmissionSource method, modify the custom_factor subscription:
    source.get('custom_factor')?.valueChanges.subscribe(custom => {
      const customValue = source.get('custom_value');
      const factorUnit = source.get('factor_unit');
      const factorSource = source.get('factor_source');

      if (custom) {
        customValue?.setValidators([Validators.required, Validators.min(0)]);
        factorUnit?.setValidators([Validators.required]);
        factorSource?.setValidators([Validators.required, this.urlValidator]);
      } else {
        customValue?.clearValidators();
        factorUnit?.clearValidators();
        factorSource?.clearValidators();
        factorSource?.setValidators(this.urlValidator); // Keep URL validator even when not custom
      }

      customValue?.updateValueAndValidity();
      factorUnit?.updateValueAndValidity();
      factorSource?.updateValueAndValidity();

      this.saveToLocalStorage(); // Save when toggling
    });

    this.emissionSources.push(source);
  }

  removeEmissionSource(index: number): void {
    this.emissionSources.removeAt(index);
  }

  searchDescription(term: any, index: number): void {
    this.selectedSourceIndex = index;
    const searchTerm = typeof term === 'string' ? term : term?.target?.value;
    if (searchTerm && searchTerm.length > 2) {
      this.isLoadingSuggestions = true;
      this.showSuggestions = true;
      this.descriptionSearchTerms.next(searchTerm);
    } else {
      this.suggestions = [];
      this.showSuggestions = false;
      this.isLoadingSuggestions = false;
    }
  }
  translationResult: string = '';
  async italianToEnglish(searchTerm: string) {
    const italianText = searchTerm;
    try {
      const response = await this.climatiqService.translateText(
        italianText,
        'IT',
        'EN'
      );

      if (response?.translations?.[0]?.text) {
        this.translationResult = response.translations[0].text;
      } else {
        console.error('Unexpected response format', response);
        this.translationResult = 'Translation failed - check console';
      }
    } catch (err) {
      console.error('Translation error:', err);
      this.translationResult = 'Error: ' + (err as Error).message;
    }
  }

  private translateText(text: string, sourceLang: string, targetLang: string) {
    const url = '/api/v2/translate';
    const body = new HttpParams()
      .set('auth_key', '3263faad-c11a-471d-b04c-110db9686ff8:fx')
      .set('text', text)
      .set('target_lang', targetLang)
      .set('source_lang', sourceLang);

    return this.http.post<any>(url, body.toString(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: 'application/json'
      }
    });
  }

  suggestion_id: any;
  selectSuggestion(suggestion: any): void {
    if (suggestion) {
      this.suggestion_id = suggestion.suggestion_id;

      // Parse the unit from the suggestion (format is usually "kg CO2e/unit")
      const [factorUnitPart, activityUnitPart] = suggestion.unit.split('/');
      const factorUnit = factorUnitPart.trim().split(' ')[0]; // Get just 'kg' from 'kg CO2e'
      const activityUnit = activityUnitPart.trim();

      // Determine unit type based on the activity unit
      let unitType = 'number'; // default
      if (['g', 'kg', 't', 'lb', 'oz'].includes(activityUnit.toLowerCase())) {
        unitType = 'weight';
      } else if (
        ['ml', 'l', 'm3', 'gal', 'bbl'].includes(activityUnit.toLowerCase())
      ) {
        unitType = 'volume';
      } else if (['m', 'km', 'mi', 'ft'].includes(activityUnit.toLowerCase())) {
        unitType = 'distance';
      } else if (
        [
          'usd',
          'eur',
          'gbp',
          'cad',
          'aud',
          'chf',
          'sek',
          'nok',
          'dkk',
          'jpy',
          'cny'
        ].includes(activityUnit.toLowerCase())
      ) {
        unitType = 'money';
      }

      if (
        this.selectedSourceIndex >= 0 &&
        this.selectedSourceIndex < this.emissionSources.length
      ) {
        const source = this.emissionSources.at(this.selectedSourceIndex);

        source.patchValue({
          factor_value:
            this.language === 'italian'
              ? suggestion.name_it
              : suggestion.name_en,
          activity_unit: activityUnit,
          factor_unit: factorUnit,
          unit_type: unitType
        });

        source.markAsDirty();
      }

      this.showSuggestions = false;
      this.saveToLocalStorage();
    }
  }

  getUnitOptionsForSource(index: number): any[] {
    if (index >= 0 && index < this.emissionSources.length) {
      const source = this.emissionSources.at(index);
      const isCustomFactor = source.get('custom_factor')?.value;

      if (isCustomFactor) {
        return this.activityUnitOptions;
      } else {
        const unitType = source.get('unit_type')?.value || 'number';
        return this.unitTypeOptions[unitType] || [];
      }
    }
    return [];
  }
  closeSuggestions(): void {
    this.showSuggestions = false;
  }

  noNumbersOnlyValidator(control: FormControl) {
    if (/^\d+$/.test(control.value)) {
      return { numbersOnly: true };
    }
    return null;
  }

  urlValidator(control: any) {
    if (!control.value || control.value.trim() === '') {
      return null;
    }

    try {
      new URL(control.value);

      if (
        !control.value.startsWith('https://') &&
        !control.value.startsWith('http://')
      ) {
        return { invalidUrl: true };
      }

      return null;
    } catch (_) {
      return { invalidUrl: true };
    }
  }

  saveToLocalStorage(): void {
    const answers = this.emissionSources.controls.map(source => ({
      description: source.get('description')?.value,
      activity_value: source.get('activity_value')?.value,
      activity_unit: source.get('activity_unit')?.value,
      factor_value: source.get('factor_value')?.value,
      custom_factor: source.get('custom_factor')?.value,
      custom_value: source.get('custom_value')?.value,
      factor_unit: source.get('factor_unit')?.value,
      factor_source: source.get('factor_source')?.value,
      justification: source.get('justification')?.value,
      suggestion_id: this.suggestion_id,
      unit_type: source.get('unit_type')?.value
    }));

    let categories = (this.categoryService.categories() as any[]) || [];
    while (categories.length < 6) categories.push(this.createDefaultCategory());
    if (!categories[5].questions)
      categories[5].questions = [this.createDefaultQuestion()];
    if (categories[5].questions.length === 0)
      categories[5].questions.push(this.createDefaultQuestion());

    categories[5].questions[0].answers = this.applicable ? answers : null;
    this.categoryService.persistCategories(categories);
  }

  private createDefaultCategory() {
    return {
      id: '',
      done: false,
      title: '',
      heading: '',
      doubt: '',
      emissions: [],
      questions: [this.createDefaultQuestion()]
    };
  }

  private createDefaultQuestion() {
    return {
      id: '',
      done: false,
      title: '',
      heading: '',
      doubt: '',
      emissions: [],
      answers: null
    };
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: MouseEvent) {
    const clickedInside = this.dropdownContainers.some(ref =>
      ref.nativeElement.contains(event.target)
    );

    if (!clickedInside) {
      // Only close if not clicking on a suggestion
      const clickedSuggestion =
        event.target instanceof Element &&
        event.target.closest('.hover\\:bg-gray-100');
      if (!clickedSuggestion) {
        this.closeSuggestions();
      }
    }
  }
  onSubmit(): void {
    if (this.form.valid) {
      this.saveToLocalStorage();
      // Add any additional submission logic here
    } else {
      // Mark all fields as touched to show validation errors
      this.markFormGroupTouched(this.form);
    }
  }

  private markFormGroupTouched(formGroup: FormGroup | FormArray) {
    Object.values(formGroup.controls).forEach(control => {
      if (control instanceof FormGroup || control instanceof FormArray) {
        this.markFormGroupTouched(control);
      } else {
        control.markAsTouched();
      }
    });
  }
  toggleCheckbox(group: any, controlName: string): void {
    const checkboxControl = group.get(controlName);
    const unitControl = group.get('factor_unit');
    const customValueControl = group.get('custom_value');
    const sourceControl = group.get('factor_source');
    const factorValueControl = group.get('factor_value');

    if (checkboxControl) {
      checkboxControl.setValue(!checkboxControl.value);
    }

    if (!checkboxControl?.value) {
      unitControl?.reset();
      customValueControl?.reset();
      sourceControl?.reset();
    } else {
      factorValueControl?.reset();
    }
  }
}
